import { useState } from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useGetBusinessAddons } from '@/apis/business/addons/hook';
import { useGetBusinessProfile } from '@/apis/business/hook';
import { BusinessAddon } from '@/apis/business/addons/types';
import AddonCard from './components/AddonCard';
import ActivateAddonDrawer, { ActivateAddonDrawerMode } from './components/ActivateAddonDrawer';
import DeactivateAddonDrawer from './components/DeactivateAddonDrawer';
import AddonTermsDrawer from './components/AddonTermsDrawer';
import SmsEligibilityDrawer from './components/SmsEligibilityDrawer';
import ReverseCancellationDialog from './components/ReverseCancellationDialog';
import { sortAddonsForDisplay } from './components/addonDisplay';

export default function Addons() {
  const { data, isLoading } = useGetBusinessAddons();
  const { data: businessProfile } = useGetBusinessProfile({ include: 'business_plan' });
  const [activateAddon, setActivateAddon] = useState<BusinessAddon | null>(null);
  const [activateMode, setActivateMode] = useState<ActivateAddonDrawerMode>('activate');
  const [deactivateAddon, setDeactivateAddon] = useState<BusinessAddon | null>(null);
  const [termsAddon, setTermsAddon] = useState<BusinessAddon | null>(null);
  const [smsEligibilityAddon, setSmsEligibilityAddon] = useState<BusinessAddon | null>(null);
  const [keepActiveAddon, setKeepActiveAddon] = useState<BusinessAddon | null>(null);

  const orderedAddons = sortAddonsForDisplay(data?.data ?? []);

  const startActivate = (addon: BusinessAddon, mode: ActivateAddonDrawerMode = 'activate') => {
    if (addon.code === 'sms_hosting' && mode === 'activate') {
      setSmsEligibilityAddon(addon);
      return;
    }
    setActivateMode(mode);
    setActivateAddon(addon);
  };

  const startManage = (addon: BusinessAddon) => {
    if (addon.code === 'concurrency_limit') {
      startActivate(addon, 'manage');
      return;
    }
    setTermsAddon(addon);
  };

  return (
    <Box>
      <Typography variant='h6' fontWeight={600}>
        Add-ons
      </Typography>
      <Typography color='secondary' fontSize={14} mt={0.5} maxWidth={720}>
        Extend your agent with optional features. Pricing and terms below are set by your agency — enable an add-on
        anytime.
      </Typography>

      {isLoading ? (
        <Grid container spacing={2} mt={2.5}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Grid key={index} size={{ xs: 12, md: 6 }}>
              <Skeleton variant='rounded' height={220} />
            </Grid>
          ))}
        </Grid>
      ) : orderedAddons.length === 0 ? (
        <Stack
          alignItems='center'
          justifyContent='center'
          mt={3}
          py={6}
          px={2}
          border='1px dashed #E0E0E0'
          borderRadius={2}
        >
          <Typography color='text.secondary'>No add-ons are offered by your agency yet.</Typography>
        </Stack>
      ) : (
        <Grid container spacing={2} mt={2.5}>
          {orderedAddons.map((addon) => (
            <Grid key={addon.id} size={{ xs: 12, md: 6 }}>
              <AddonCard
                addon={addon}
                onActivate={() => startActivate(addon)}
                onTerms={() => setTermsAddon(addon)}
                onManage={() => startManage(addon)}
                onDeactivate={() => setDeactivateAddon(addon)}
                onKeepActive={() => setKeepActiveAddon(addon)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <AddonTermsDrawer
        addon={termsAddon}
        open={Boolean(termsAddon)}
        onClose={() => setTermsAddon(null)}
        onActivate={() => {
          if (!termsAddon) return;
          const next = termsAddon;
          setTermsAddon(null);
          startActivate(next);
        }}
      />

      <SmsEligibilityDrawer
        addon={smsEligibilityAddon}
        open={Boolean(smsEligibilityAddon)}
        onClose={() => setSmsEligibilityAddon(null)}
        onContinue={() => {
          if (!smsEligibilityAddon) return;
          const next = smsEligibilityAddon;
          setSmsEligibilityAddon(null);
          setActivateMode('activate');
          setActivateAddon(next);
        }}
      />

      <ActivateAddonDrawer
        addon={activateAddon}
        open={Boolean(activateAddon)}
        mode={activateMode}
        includedConcurrencyPerLocation={businessProfile?.business_plan?.included_concurrency_per_location}
        onClose={() => setActivateAddon(null)}
      />

      <DeactivateAddonDrawer
        addon={deactivateAddon}
        open={Boolean(deactivateAddon)}
        onClose={() => setDeactivateAddon(null)}
      />

      <ReverseCancellationDialog
        addon={keepActiveAddon}
        open={Boolean(keepActiveAddon)}
        onClose={() => setKeepActiveAddon(null)}
      />
    </Box>
  );
}
